import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { BarChart3, Eye, EyeOff, ShieldCheck, TrendingUp, Users } from "lucide-react";
import Turnstile, { turnstileEnabled } from '../../components/Turnstile';
import { AuthContext } from "../../context/Auth";
import { useTheme } from "../../hooks/useTheme";
import { Button, Field, inputClass } from "../../components/ui";

const HIGHLIGHTS = [
  [Users, 'Usuários e assinaturas', 'Busque, edite, ative ou desative contas.'],
  [TrendingUp, 'Funil de ativação', 'Do cadastro ao pagamento, com as metas de cada etapa.'],
  [BarChart3, 'Relatórios', 'O que a Nath registra, as categorias e as integrações.'],
];

export const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [captcha, setCaptcha] = useState(null);
    const [captchaKey, setCaptchaKey] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const { signIn, signed } = useContext(AuthContext);
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    const handleSignIn = async (e) => {
        e.preventDefault();

        if (!email || !password) {
            toast.error("Preencha o e-mail e a senha.");
            return;
        }
        if (turnstileEnabled && !captcha) {
            toast.error("Confirme a verificação antes de entrar.");
            return;
        }

        try {
            setIsLoading(true);
            await signIn({ email: email.trim(), password, turnstileToken: captcha });
        } catch (error) {
            // Cada token do captcha vale uma vez: pede outro
            setCaptcha(null);
            setCaptchaKey((k) => k + 1);
            // A mensagem do backend ("E-mail, telefone ou senha incorretos") vem antes do erro técnico do axios
            toast.error(error.response?.data?.msg || error.message || "Ocorreu um erro ao fazer login.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (signed) navigate('/dashboard');
    }, [signed, navigate]);

    return (
        <div className="grid min-h-screen lg:grid-cols-2">
            <div className="flex flex-col px-6 py-8 sm:px-12">
                <img src={isDarkMode ? '/logocontrolly.png' : '/logocontrollyblack.png'} alt="Controlly" className="h-auto w-[132px]" />
                <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10">
                    <span className="chat-up inline-flex w-fit items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                        <ShieldCheck className="size-3.5" /> Painel administrativo
                    </span>
                    <h1 className="chat-up mt-4 text-[30px] font-medium leading-tight tracking-[-0.03em] text-neutral-900 dark:text-white">Bem-vindo de volta</h1>
                    <p className="chat-up mt-2 text-[15px] text-neutral-500 dark:text-neutral-400" style={{ animationDelay: '60ms' }}>Entre com sua conta de administrador.</p>

                    <form onSubmit={handleSignIn} className="chat-up mt-8 flex flex-col gap-3" style={{ animationDelay: '120ms' }}>
                        <Field label="E-mail">
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="voce@email.com" className={`${inputClass} h-11`} />
                        </Field>
                        <Field label="Senha">
                            <span className="relative">
                                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" className={`${inputClass} h-11 pr-10`} />
                                <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600" aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>
                                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                                </button>
                            </span>
                        </Field>
                        <Turnstile onToken={setCaptcha} resetKey={captchaKey} theme={isDarkMode ? 'dark' : 'light'} />
                        <Button type="submit" loading={isLoading} className="mt-1 h-11">Entrar</Button>
                    </form>
                    <p className="mt-6 text-center text-xs text-neutral-400 dark:text-neutral-500">Apenas administradores podem acessar este painel.</p>
                </div>
            </div>

            <div className="relative hidden overflow-hidden bg-[#0b0b0c] lg:flex lg:flex-col lg:justify-center lg:px-16">
                <div className="pointer-events-none absolute -right-40 -top-40 size-[520px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(16,185,129,0.14), transparent)' }} />
                <p className="relative max-w-md text-[34px] font-medium leading-tight tracking-[-0.03em] text-white">
                    Quem usa a Nath, num lugar só. <span className="text-neutral-500">Usuários, crescimento e relatórios do Controlly.</span>
                </p>
                <div className="relative mt-10 flex max-w-md flex-col gap-3">
                    {HIGHLIGHTS.map(([Icon, title, text], i) => (
                        <div key={title} className="chat-up flex items-start gap-3 rounded-2xl bg-white/[0.04] p-4" style={{ animationDelay: `${200 + i * 120}ms` }}>
                            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400"><Icon className="size-4" /></span>
                            <div>
                                <p className="text-sm font-medium text-white">{title}</p>
                                <p className="mt-0.5 text-sm text-neutral-400">{text}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
